/**
 * usePageSettings (12-08-2026) — delt tilstand for sideindstillinger
 * (tandhjuls-draweren + Indstillinger → Sideindstillinger).
 *
 * Felt-definitioner/defaults kommer fra shared-registry'et
 * (PAGE_SETTINGS_DEFS); VÆRDIER + canEdit pr. side kommer fra
 * GET /api/page-settings (kun sider brugeren har view-grant til).
 * Gem: PUT /api/page-settings { routePath, values } — serveren håndhæver
 * edit-grant til TARGET-siden (fail-closed).
 *
 * Modul-scoped cache så drawer og oversigtssiden deler samme data.
 */

import { ref } from 'vue';
import type { PageSettingValue } from '@cockpit/shared';

import { fetchJson } from '../lib/http';
import { useSyncSchedules } from './useSyncSchedules';

export interface PageSettingsEntryDto {
  routePath: string;
  values: Record<string, PageSettingValue>;
  canEdit: boolean;
  updatedAt: number | null;
}

const entries = ref<PageSettingsEntryDto[]>([]);
const loaded = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);

export function usePageSettings() {
  async function load(force = false): Promise<void> {
    if (loading.value || (loaded.value && !force)) return;
    loading.value = true;
    try {
      const res = await fetchJson<{ pages: PageSettingsEntryDto[] }>('/api/page-settings');
      entries.value = res.pages;
      loaded.value = true;
      error.value = null;
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      loading.value = false;
    }
  }

  function entryFor(routePath: string): PageSettingsEntryDto | undefined {
    return entries.value.find((e) => e.routePath === routePath);
  }

  async function save(
    routePath: string,
    values: Record<string, PageSettingValue>,
  ): Promise<void> {
    const res = await fetchJson<{ ok: boolean; values: Record<string, PageSettingValue> }>(
      '/api/page-settings',
      { method: 'PUT', body: JSON.stringify({ routePath, values }) },
    );
    const entry = entryFor(routePath);
    if (entry) entry.values = res.values;
    // Synk-intervaller ([D-356]) deles af flere sider og vises med status:
    // hent igen, så andre sider og oversigtens tabel viser den nye værdi.
    if (Object.keys(values).some((k) => k.startsWith('sync_'))) {
      void load(true);
      void useSyncSchedules().load(true);
    }
  }

  return { entries, loaded, loading, error, load, entryFor, save };
}
